/**
 * caisse/utils/printHelpers.js — Vague 2 refactor CaissePage (Feb 2026)
 *
 * Extrait les 4 fonctions d'impression tickets (~500 lignes) hors du fichier
 * principal. Fonctions PURES qui ouvrent une nouvelle fenêtre navigateur et
 * y injectent du HTML formaté.
 *
 *   • printKitchenOrder(invoice)  — bon de production Cuisine
 *   • printBarOrder(invoice)      — bon de production Bar
 *   • printGamesOrder(invoice)    — bon de production Jeux/Fast Food
 *   • printTicket(invoice, currentUser?) — ticket client final (PDF ou fallback HTML)
 */
import axios from "axios";
import QRCode from "qrcode";
import { format } from "date-fns";
import { toast } from "sonner";
import { LOGO_BASE64 } from "../constants_logo";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const formatPrice = (price) => new Intl.NumberFormat("fr-FR").format(price || 0);

// Réutilisé uniquement pour retrouver les libellés méthode de paiement dans le ticket
const PAYMENT_METHODS = [
  { value: "cash", label: "Espèces" },
  { value: "card", label: "Carte bancaire" },
  { value: "mobile", label: "Mobile Money" },
  { value: "kkiapay", label: "KKiapay" },
  { value: "wallet", label: "Porte-monnaie" },
  { value: "check", label: "Chèque" },
];

const formatValidatorLabel = (user) => {
  if (!user) return "—";
  const fn = user.full_name || user.name || user.username || "";
  const role = user.role ? ` (${user.role})` : "";
  return fn + role;
};

export const printKitchenOrder = (invoice) => {
  const printWindow = window.open('', '_blank', 'width=350,height=700');
  
  // CUISINE = articles salle_jardin (restaurant) + accompagnements
  const kitchenItems = (invoice.items || []).filter(item => 
    item.department === 'salle_jardin' || item.department === 'accompagnements'
  );
  const totalItems = kitchenItems.reduce((sum, item) => sum + item.quantity, 0);
  
  // If no kitchen items, show message
  if (totalItems === 0) {
    printWindow.document.write(`
      <!DOCTYPE html>
      <html><head><title>Aucun article cuisine</title></head>
      <body style="font-family: Arial; padding: 20px; text-align: center;">
        <h2>Aucun article pour la cuisine</h2>
        <p>Cette commande ne contient pas de plats ni d'accompagnements.</p>
        <script>setTimeout(function() { window.close(); }, 2000);</script>
      </body></html>
    `);
    printWindow.document.close();
    return;
  }
  
  // Séparer plats et accompagnements
  const plats = kitchenItems.filter(item => item.department === 'salle_jardin');
  const accompagnements = kitchenItems.filter(item => item.department === 'accompagnements');
  
  const platsHtml = plats.length > 0 ? plats.map(item => `
    <div class="item-row">
      <div class="qty-badge">${item.quantity}</div>
      <div class="item-name">${item.name}</div>
    </div>
  `).join('') : '';
  
  const accompagnementsHtml = accompagnements.length > 0 ? `
    <div class="section-divider">ACCOMPAGNEMENTS</div>
    ${accompagnements.map(item => `
      <div class="item-row accomp">
        <div class="qty-badge accomp-badge">${item.quantity}</div>
        <div class="item-name">${item.name}</div>
      </div>
    `).join('')}
  ` : '';

  // Get table number and time
  const tableNum = invoice.table_number || '—';
  const orderTime = new Date(invoice.created_at).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'});
  const orderDate = new Date(invoice.created_at).toLocaleDateString('fr-FR', {day: '2-digit', month: '2-digit'});

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>CUISINE - ${invoice.invoice_number}</title>
        <meta charset="UTF-8">
        <style>
          @page { size: 80mm auto; margin: 0; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Courier New', monospace; width: 80mm; padding: 3mm; font-size: 12px; line-height: 1.3; background: #fff; color: #000; }
          .header { text-align: center; padding: 8px 0; border-bottom: 3px solid #000; margin-bottom: 8px; }
          .kitchen-title { font-size: 28px; font-weight: 900; letter-spacing: 3px; }
          .subtitle { font-size: 11px; margin-top: 2px; }
          .table-box { border: 3px solid #000; text-align: center; padding: 8px; margin: 8px 0; }
          .table-label { font-size: 12px; font-weight: 600; }
          .table-number { font-size: 56px; font-weight: 900; line-height: 1; }
          .table-time { font-size: 14px; font-weight: 600; margin-top: 4px; }
          .meta-section { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px dashed #000; margin-bottom: 8px; font-size: 11px; }
          .items-section { border: 2px solid #000; margin: 8px 0; }
          .items-header { background: #000; color: #fff; padding: 6px 8px; display: flex; align-items: center; justify-content: space-between; }
          .items-title { font-size: 13px; font-weight: 800; letter-spacing: 1px; }
          .items-count { background: #fff; color: #000; padding: 2px 8px; font-size: 12px; font-weight: 900; }
          .item-row { display: flex; align-items: center; padding: 6px 8px; border-bottom: 1px dashed #ccc; }
          .item-row:last-child { border-bottom: none; }
          .qty-badge { background: #000; color: #fff; min-width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 900; margin-right: 10px; }
          .item-name { font-size: 14px; font-weight: 600; }
          .section-divider { background: #333; color: #fff; padding: 4px 8px; font-size: 11px; font-weight: 700; text-align: center; letter-spacing: 1px; margin-top: 2px; }
          .item-row.accomp { background: #f5f5f5; }
          .accomp-badge { background: #666 !important; }
          .summary-box { border: 3px solid #000; padding: 8px; margin: 10px 0; text-align: center; }
          .summary-label { font-size: 11px; font-weight: 600; }
          .summary-value { font-size: 32px; font-weight: 900; line-height: 1; }
          .footer { text-align: center; margin-top: 8px; padding-top: 8px; border-top: 3px solid #000; }
          .footer-badge { font-size: 12px; font-weight: 800; letter-spacing: 1px; }
          .footer-time { font-size: 9px; margin-top: 4px; }
          .cut-line { margin-top: 10px; padding-top: 6px; border-top: 1px dashed #000; text-align: center; font-size: 10px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="kitchen-title">CUISINE</div>
          <div class="subtitle">Facture</div>
        </div>
        <div class="table-box">
          <div class="table-label">TABLE N°</div>
          <div class="table-number">${tableNum}</div>
          <div class="table-time">${orderTime}</div>
        </div>
        <div class="meta-section">
          <span><strong>Agent:</strong> ${invoice.created_by || 'N/A'}</span>
          <span><strong>Date:</strong> ${orderDate}</span>
          <span><strong>#${invoice.invoice_number.split('-').pop()}</strong></span>
        </div>
        <div class="items-section">
          <div class="items-header">
            <span class="items-title">PLATS & ACCOMP.</span>
            <span class="items-count">${totalItems}</span>
          </div>
          ${platsHtml}
          ${accompagnementsHtml}
        </div>
        <div class="summary-box">
          <div class="summary-label">TOTAL ARTICLES</div>
          <div class="summary-value">${totalItems}</div>
        </div>
        <div class="footer">
          <div class="footer-badge">*** BON CUISINE ***</div>
          <div class="footer-time">Imprimé ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}</div>
        </div>
        <div class="cut-line">- - - - - - - - - - - - - - - - - -</div>
        <script>
          window.onload = function() {
            setTimeout(function() { window.print(); }, 300);
            setTimeout(function() { window.close(); }, 1000);
          }
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
};

// ============== BON DE COMMANDE BAR (80mm) ==============
export const printBarOrder = (invoice) => {
  const printWindow = window.open('', '_blank', 'width=350,height=700');
  
  // Filter only bar items
  const barItems = (invoice.items || []).filter(item => item.department === 'bar');
  const totalItems = barItems.reduce((sum, item) => sum + item.quantity, 0);
  
  // If no bar items, show message
  if (totalItems === 0) {
    printWindow.document.write(`
      <!DOCTYPE html>
      <html><head><title>Aucun article bar</title></head>
      <body style="font-family: Arial; padding: 20px; text-align: center;">
        <h2>Aucun article pour le bar</h2>
        <p>Cette commande ne contient pas d'articles Bar.</p>
        <script>setTimeout(function() { window.close(); }, 2000);</script>
      </body></html>
    `);
    printWindow.document.close();
    return;
  }
  
  const itemsHtml = barItems.map(item => `
    <div class="item-row">
      <div class="qty-badge">${item.quantity}</div>
      <div class="item-name">${item.name}</div>
    </div>
  `).join('');

  const tableNum = invoice.table_number || '—';
  const orderTime = new Date(invoice.created_at).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'});
  const orderDate = new Date(invoice.created_at).toLocaleDateString('fr-FR', {day: '2-digit', month: '2-digit'});

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>BAR - ${invoice.invoice_number}</title>
        <meta charset="UTF-8">
        <style>
          @page { size: 80mm auto; margin: 0; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Courier New', monospace; width: 80mm; padding: 3mm; font-size: 12px; line-height: 1.3; background: #fff; color: #000; }
          .header { text-align: center; padding: 8px 0; border-bottom: 3px solid #000; margin-bottom: 8px; }
          .bar-title { font-size: 28px; font-weight: 900; letter-spacing: 3px; }
          .subtitle { font-size: 11px; margin-top: 2px; }
          .table-box { border: 3px solid #000; text-align: center; padding: 8px; margin: 8px 0; }
          .table-label { font-size: 12px; font-weight: 600; }
          .table-number { font-size: 56px; font-weight: 900; line-height: 1; }
          .table-time { font-size: 14px; font-weight: 600; margin-top: 4px; }
          .meta-section { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px dashed #000; margin-bottom: 8px; font-size: 11px; }
          .items-section { border: 2px solid #000; margin: 8px 0; }
          .items-header { background: #000; color: #fff; padding: 6px 8px; display: flex; align-items: center; justify-content: space-between; }
          .items-title { font-size: 13px; font-weight: 800; letter-spacing: 1px; }
          .items-count { background: #fff; color: #000; padding: 2px 8px; font-size: 12px; font-weight: 900; }
          .item-row { display: flex; align-items: center; padding: 6px 8px; border-bottom: 1px dashed #ccc; }
          .item-row:last-child { border-bottom: none; }
          .qty-badge { background: #000; color: #fff; min-width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 900; margin-right: 10px; }
          .item-name { font-size: 14px; font-weight: 600; }
          .summary-box { border: 3px solid #000; padding: 8px; margin: 10px 0; text-align: center; }
          .summary-label { font-size: 11px; font-weight: 600; }
          .summary-value { font-size: 32px; font-weight: 900; line-height: 1; }
          .footer { text-align: center; margin-top: 8px; padding-top: 8px; border-top: 3px solid #000; }
          .footer-badge { font-size: 12px; font-weight: 800; letter-spacing: 1px; }
          .footer-time { font-size: 9px; margin-top: 4px; }
          .cut-line { margin-top: 10px; padding-top: 6px; border-top: 1px dashed #000; text-align: center; font-size: 10px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="bar-title">BAR</div>
          <div class="subtitle">Facture</div>
        </div>
        <div class="table-box">
          <div class="table-label">TABLE N°</div>
          <div class="table-number">${tableNum}</div>
          <div class="table-time">${orderTime}</div>
        </div>
        <div class="meta-section">
          <span><strong>Agent:</strong> ${invoice.created_by || 'N/A'}</span>
          <span><strong>Date:</strong> ${orderDate}</span>
          <span><strong>#${invoice.invoice_number.split('-').pop()}</strong></span>
        </div>
        <div class="items-section">
          <div class="items-header">
            <span class="items-title">BOISSONS</span>
            <span class="items-count">${totalItems}</span>
          </div>
          ${itemsHtml}
        </div>
        <div class="summary-box">
          <div class="summary-label">TOTAL ARTICLES</div>
          <div class="summary-value">${totalItems}</div>
        </div>
        <div class="footer">
          <div class="footer-badge">*** BON BAR ***</div>
          <div class="footer-time">Imprimé ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}</div>
        </div>
        <div class="cut-line">- - - - - - - - - - - - - - - - - -</div>
        <script>
          window.onload = function() {
            setTimeout(function() { window.print(); }, 300);
            setTimeout(function() { window.close(); }, 1000);
          }
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
};

// ============== BON DE COMMANDE JEUX (80mm) ==============
export const printGamesOrder = (invoice) => {
  const printWindow = window.open('', '_blank', 'width=350,height=700');
  
  // Filter only games items
  const gamesItems = (invoice.items || []).filter(item => item.department === 'jeux');
  const totalItems = gamesItems.reduce((sum, item) => sum + item.quantity, 0);
  
  // If no games items, show message
  if (totalItems === 0) {
    printWindow.document.write(`
      <!DOCTYPE html>
      <html><head><title>Aucun article jeux</title></head>
      <body style="font-family: Arial; padding: 20px; text-align: center;">
        <h2>Aucun article pour les jeux</h2>
        <p>Cette commande ne contient pas d'articles Jeux.</p>
        <script>setTimeout(function() { window.close(); }, 2000);</script>
      </body></html>
    `);
    printWindow.document.close();
    return;
  }
  
  const itemsHtml = gamesItems.map(item => `
    <div class="item-row">
      <div class="qty-badge">${item.quantity}</div>
      <div class="item-name">${item.name}</div>
    </div>
  `).join('');

  const tableNum = invoice.table_number || '—';
  const orderTime = new Date(invoice.created_at).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'});
  const orderDate = new Date(invoice.created_at).toLocaleDateString('fr-FR', {day: '2-digit', month: '2-digit'});

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>JEUX - ${invoice.invoice_number}</title>
        <meta charset="UTF-8">
        <style>
          @page { size: 80mm auto; margin: 0; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Courier New', monospace; width: 80mm; padding: 3mm; font-size: 12px; line-height: 1.3; background: #fff; color: #000; }
          .header { text-align: center; padding: 8px 0; border-bottom: 3px solid #000; margin-bottom: 8px; }
          .games-title { font-size: 28px; font-weight: 900; letter-spacing: 3px; }
          .subtitle { font-size: 11px; margin-top: 2px; }
          .table-box { border: 3px solid #000; text-align: center; padding: 8px; margin: 8px 0; }
          .table-label { font-size: 12px; font-weight: 600; }
          .table-number { font-size: 56px; font-weight: 900; line-height: 1; }
          .table-time { font-size: 14px; font-weight: 600; margin-top: 4px; }
          .meta-section { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px dashed #000; margin-bottom: 8px; font-size: 11px; }
          .items-section { border: 2px solid #000; margin: 8px 0; }
          .items-header { background: #000; color: #fff; padding: 6px 8px; display: flex; align-items: center; justify-content: space-between; }
          .items-title { font-size: 13px; font-weight: 800; letter-spacing: 1px; }
          .items-count { background: #fff; color: #000; padding: 2px 8px; font-size: 12px; font-weight: 900; }
          .item-row { display: flex; align-items: center; padding: 6px 8px; border-bottom: 1px dashed #ccc; }
          .item-row:last-child { border-bottom: none; }
          .qty-badge { background: #000; color: #fff; min-width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 900; margin-right: 10px; }
          .item-name { font-size: 14px; font-weight: 600; }
          .summary-box { border: 3px solid #000; padding: 8px; margin: 10px 0; text-align: center; }
          .summary-label { font-size: 11px; font-weight: 600; }
          .summary-value { font-size: 32px; font-weight: 900; line-height: 1; }
          .footer { text-align: center; margin-top: 8px; padding-top: 8px; border-top: 3px solid #000; }
          .footer-badge { font-size: 12px; font-weight: 800; letter-spacing: 1px; }
          .footer-time { font-size: 9px; margin-top: 4px; }
          .cut-line { margin-top: 10px; padding-top: 6px; border-top: 1px dashed #000; text-align: center; font-size: 10px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="games-title">JEUX</div>
          <div class="subtitle">Facture</div>
        </div>
        <div class="table-box">
          <div class="table-label">TABLE N°</div>
          <div class="table-number">${tableNum}</div>
          <div class="table-time">${orderTime}</div>
        </div>
        <div class="meta-section">
          <span><strong>Agent:</strong> ${invoice.created_by || 'N/A'}</span>
          <span><strong>Date:</strong> ${orderDate}</span>
          <span><strong>#${invoice.invoice_number.split('-').pop()}</strong></span>
        </div>
        <div class="items-section">
          <div class="items-header">
            <span class="items-title">SESSIONS</span>
            <span class="items-count">${totalItems}</span>
          </div>
          ${itemsHtml}
        </div>
        <div class="summary-box">
          <div class="summary-label">TOTAL SESSIONS</div>
          <div class="summary-value">${totalItems}</div>
        </div>
        <div class="footer">
          <div class="footer-badge">*** BON JEUX ***</div>
          <div class="footer-time">Imprimé ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}</div>
        </div>
        <div class="cut-line">- - - - - - - - - - - - - - - - - -</div>
        <script>
          window.onload = function() {
            setTimeout(function() { window.print(); }, 300);
            setTimeout(function() { window.close(); }, 1000);
          }
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
};

// ============== TICKET THERMIQUE (80mm) ==============
export const printTicket = async (invoice, currentUser = null) => {
  const printWindow = window.open('', '_blank', 'width=300,height=600');
  const itemsHtml = (invoice.items || []).map(item => `
    <tr>
      <td style="padding: 2px 0; font-size: 11px;">${item.name}</td>
      <td style="padding: 2px 0; text-align: center; font-size: 11px;">${item.quantity}</td>
      <td style="padding: 2px 0; text-align: right; font-size: 11px;">${formatPrice(item.price * item.quantity)}</td>
    </tr>
  `).join('');

  // 🆕 Fév 2026 — Message admin (petit mot) affiché sur le ticket thermique
  let adminMessage = "";
  try {
    const res = await axios.get(`${API}/admin/customer-bon-message`);
    if (res.data && res.data.active && (res.data.message || "").trim()) {
      adminMessage = res.data.message.trim();
    }
  } catch (e) { /* silencieux */ }

  // QR code "Mon ticket" (avis client) — uniquement pour les BON CLIENT validés
  // pointant vers la page publique /ticket/:id
  let qrDataUrl = "";
  let publicUrl = "";
  if (invoice.bon_number && invoice.id) {
    try {
      publicUrl = `${window.location.origin}/ticket/${invoice.id}`;
      qrDataUrl = await QRCode.toDataURL(publicUrl, {
        width: 220,
        margin: 1,
        errorCorrectionLevel: "M",
      });
    } catch (e) {
      console.warn("QR generation failed", e);
    }
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Bon ${invoice.invoice_number}</title>
        <style>
          @page { size: 80mm auto; margin: 0; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Courier New', monospace; 
            width: 80mm; 
            padding: 5mm; 
            font-size: 12px;
            line-height: 1.3;
          }
          .header { text-align: center; margin-bottom: 8px; border-bottom: 1px dashed #000; padding-bottom: 8px; }
          .logo { width: 50px; height: 50px; margin: 0 auto 5px; }
          .logo img { width: 100%; height: 100%; object-fit: contain; }
          .header p { font-size: 10px; }
          .info { margin: 8px 0; font-size: 11px; }
          .info div { display: flex; justify-content: space-between; }
          table { width: 100%; border-collapse: collapse; margin: 8px 0; }
          th { text-align: left; font-size: 10px; border-bottom: 1px solid #000; padding: 2px 0; }
          .totals { border-top: 1px dashed #000; margin-top: 8px; padding-top: 8px; }
          .totals div { display: flex; justify-content: space-between; font-size: 11px; margin: 2px 0; }
          .grand-total { font-size: 14px !important; font-weight: bold; border-top: 1px solid #000; padding-top: 5px; margin-top: 5px; }
          .footer { text-align: center; margin-top: 10px; font-size: 10px; border-top: 1px dashed #000; padding-top: 8px; }
          .server { font-size: 10px; margin-top: 5px; }
          .validation { font-size: 10px; margin-top: 3px; }
          .doc-title { font-size: 9px; font-weight: bold; text-align: center; margin: 5px 0; letter-spacing: 1px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo"><img src="${LOGO_BASE64}" alt="Logo" /></div>
          <p>Restaurant & Jeux VR</p>
          <p>Tél: +229 01 4147 0000</p>
        </div>
        
        <div class="doc-title">${invoice.bon_number ? 'BON CLIENT' : 'BON DE COMMANDE'}</div>
        
        <div class="info">
          ${invoice.bon_number ? `<div><span>Bon Client:</span><span><b style="color: #d97706; font-size: 14px;">${invoice.bon_number}</b></span></div>` : ''}
          <div><span>N° Facture:</span><span><b>${invoice.invoice_number}</b></span></div>
          <div><span>Date:</span><span>${format(new Date(invoice.created_at), "dd/MM/yy HH:mm")}</span></div>
          <div><span>Client:</span><span>${invoice.customer_name}</span></div>
          ${invoice.table_number != null ? `<div><span>Table:</span><span><b>${invoice.table_number}</b></span></div>` : ''}
        </div>
        
        <table>
          <thead>
            <tr>
              <th style="width: 50%;">Article</th>
              <th style="text-align: center;">Qté</th>
              <th style="text-align: right;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHtml}
          </tbody>
        </table>
        
        <div class="totals">
          <div><span>Sous-total:</span><span>${formatPrice(invoice.subtotal)} F</span></div>
          ${invoice.discount > 0 ? `<div><span>Remise (${invoice.discount}%):</span><span>-${formatPrice(invoice.discount_amount)} F</span></div>` : ''}
          <div class="grand-total"><span>TOTAL:</span><span>${formatPrice(invoice.total)} FCFA</span></div>
          <div><span>Paiement:</span><span>${PAYMENT_METHODS.find(p => p.value === invoice.payment_method)?.label || invoice.payment_method}</span></div>
        </div>
        
        <div class="server">
          Agent: ${invoice.created_by || '-'}
        </div>
        ${invoice.validation_status === 'validated' ? `
        <div class="validation">
          Validé par: ${invoice.validated_by || formatValidatorLabel(currentUser)}
        </div>
        ` : `
        <div class="validation" style="color: red;">
          En attente de validation
        </div>
        `}
        
        <div class="footer">
          ${adminMessage ? `
            <div style="margin: 10px 0 8px; padding: 6px 4px; border: 2px solid #000; background: #f5f5f5; border-radius: 3px;">
              <p style="font-size: 12px; font-weight: bold; text-align: center; line-height: 1.35;">
                ${adminMessage}
              </p>
            </div>
          ` : ''}
          ${qrDataUrl ? `
            <div style="margin: 10px auto 8px; padding: 8px 4px; border: 1px dashed #000; border-radius: 4px;">
              <p style="font-size: 10px; font-weight: bold; margin-bottom: 4px;">Scannez pour donner votre avis</p>
              <img src="${qrDataUrl}" alt="QR Code" style="width: 110px; height: 110px; display: block; margin: 0 auto;" />
              <p style="font-size: 8px; color: #555; margin-top: 4px; word-break: break-all;">${publicUrl}</p>
            </div>
          ` : ''}
          <p>Merci de votre visite !</p>
          <p>À bientôt</p>
        </div>
      </body>
    </html>
  `);
  printWindow.document.close();
  setTimeout(() => printWindow.print(), 250);
};

// Download PDF from backend
export const downloadPDF = async (invoice) => {
  if (!invoice.id || invoice.id === "PREVIEW") {
    toast.error("Veuillez d'abord enregistrer le facture");
    return;
  }
  try {
    const response = await axios.get(`${API}/invoices/${invoice.id}/pdf`, {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `facture_${invoice.invoice_number || invoice.id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success("PDF téléchargé");
  } catch (error) {
    console.error("Error downloading PDF:", error);
    toast.error("Erreur lors du téléchargement du PDF");
  }
};

// ============== CRUD OPERATIONS ==============
